// The message the editor hands the game when you press Play.
//
// It lives in `sim/` for the same reason `replay.ts` does: it is a description of
// a world, and both packages need to agree on it exactly. The editor writes one,
// the game reads one, and neither imports the other.
//
// Everything a room needs to be *played the way it was designed* travels in the
// payload, not just the room:
//
//   room      as edited, not as committed -- the whole point is testing the edit
//   physics   including tuned dials; a room verified under different numbers than
//             it was designed under has verified a different room
//   spawn     where the caster stands and which way they face, since Project
//             fires along the facing and half the puzzles are about approach
//   runes     what the player can cast with, which is the difference between "is
//             this solvable" and "is this solvable *yet*"
//
// That is deliberately the same self-contained-snapshot shape as `Recording`, and
// for the same reason: a payload that referenced the shipped rooms would test the
// committed world rather than the one on screen.
import { Facing, RoomDef } from './types';
import { PhysicsConfig, withPhysicsDefaults } from './physics';
import { WORLD_FORMAT_VERSION, loadRoom } from './rooms/load';
import { readableJson } from './rooms/save';

/** Bumped when the payload's shape changes. The game refuses a version it does
 * not know rather than reading a familiar-looking field out of an unfamiliar
 * message -- the two halves ship as separate builds and can be out of step. */
export const PLAYTEST_FORMAT_VERSION = 1;

/** The two-message handshake, named here so the editor and the game cannot drift
 * apart on a string literal. The game says READY once its listener is actually
 * attached; the editor replies with PAYLOAD. That order matters -- a ready sent at
 * boot would race the room against its own handler. */
export const PLAYTEST_READY_MESSAGE = 'rune-world-playtest-ready';
export const PLAYTEST_PAYLOAD_MESSAGE = 'rune-world-playtest';
/** The hash the editor opens the game with, and the flag the game checks. */
export const PLAYTEST_HASH = 'playtest';

export interface PlaytestPayload {
  formatVersion: number;
  room: RoomDef;
  physics: PhysicsConfig;
  spawn: { x: number; y: number; facing: Facing };
  /** Rune ids the player starts holding. Derived by default (see
   * `progression.ts`), overridable, and *authoritative*: the game replaces the
   * learned set with exactly this rather than adding to it, so a playtest can
   * ask "what if they got here without Reverse" as easily as "with everything". */
  runes: string[];
}

export class PlaytestError extends Error {}

/** Checks a payload that arrived from somewhere else, and returns it with the
 * room run through the real loader.
 *
 * The game trusting this message is exactly as dangerous as the game trusting a
 * room file -- an unvalidated room crashes the renderer somewhere far from the
 * cause -- so it goes through `loadRoom` like every other room does. */
export function loadPlaytest(raw: unknown): PlaytestPayload {
  const payload = raw as Partial<PlaytestPayload> | null;
  if (!payload || typeof payload !== 'object') throw new PlaytestError('playtest payload is not an object');
  if (payload.formatVersion !== PLAYTEST_FORMAT_VERSION) {
    throw new PlaytestError(`playtest format version ${payload.formatVersion}, expected ${PLAYTEST_FORMAT_VERSION}`);
  }
  if (!payload.physics) throw new PlaytestError('playtest payload carries no physics');
  if (!Array.isArray(payload.runes)) throw new PlaytestError('playtest payload carries no rune list');
  // Today's defaults *under* the payload's own config, never over it -- the same
  // helper, for the same reason, that `worldFromRecording` uses (see replay.ts).
  // The two halves ship as separate builds, so an editor a version behind sends a
  // config missing a dial added since, and the game would then read
  // `physics.clock.msPerTurn` off `undefined` and take the renderer down a long
  // way from the cause. Filling only what is *absent* keeps every value the
  // editor actually chose authoritative while a new dial defaults instead of
  // throwing.
  //
  // This is also why adding the turn clock needed no `PLAYTEST_FORMAT_VERSION`
  // bump. A version bump exists to refuse a message whose familiar-looking fields
  // would be *misread*; a physics group that simply is not there yet is not
  // misread by defaulting it, any more than an old recording is.
  const physics = withPhysicsDefaults(payload.physics);
  const spawn = payload.spawn;
  if (!spawn || !Number.isInteger(spawn.x) || !Number.isInteger(spawn.y) || !spawn.facing) {
    throw new PlaytestError('playtest payload carries no spawn');
  }
  const room = loadRoom({ ...payload.room, formatVersion: WORLD_FORMAT_VERSION });
  if (spawn.x < 0 || spawn.y < 0 || spawn.x >= room.cols || spawn.y >= room.rows) {
    throw new PlaytestError(`spawn (${spawn.x},${spawn.y}) is outside ${room.id}`);
  }
  return { formatVersion: PLAYTEST_FORMAT_VERSION, room, physics, spawn, runes: [...payload.runes] };
}

/** Readable on purpose: a payload that can be pasted into an issue, or saved next
 * to a bug report, is worth more than a compact one. */
export function stringifyPlaytest(payload: PlaytestPayload): string {
  return readableJson(payload) + '\n';
}

// ---------------------------------------------------------------------------
// Carrying a payload in a URL.
//
// The postMessage handshake below is the better path when it works: no size
// limit, no encoding, and the game reloads in place. It does not work on a phone.
// Mobile browsers block `window.open` far more aggressively than desktop ones,
// several contexts hand the opened page a null `window.opener`, and without an
// opener there is nobody to hand the room to -- the game then boots normally,
// which looks exactly like the button doing nothing.
//
// A link has none of those problems. Tapping one is ordinary navigation, blocked
// by nothing, and needs no second window to stay alive. The cost is that the
// payload has to fit in a URL, which is what the compression here is for: room9
// is 25 KB of JSON and deflates to 3 KB, about 4 KB of base64url. It rides in the
// hash, which browsers never send to a server, so no request-size limit applies
// either.
//
// It is also the shareable form the design session wanted: a playtest link can be
// pasted into a message and opened by someone else.
// ---------------------------------------------------------------------------

/** Marks the encoding, so a link made by an older build is refused rather than
 * misread. `r` is raw JSON; `d` is deflate-raw. */
type Encoding = 'r' | 'd';

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(text: string): Uint8Array {
  const padded = text.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

async function through(stream: ReadableStream<Uint8Array>): Promise<Uint8Array> {
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** Pushes bytes through a (de)compression stream and collects the result.
 *
 * The copy into a freshly allocated view is not busywork. TypeScript types a
 * `Uint8Array` as possibly backed by a `SharedArrayBuffer`, which is not a
 * `BufferSource`, so the writer will not take one; `new Uint8Array(length)` is
 * concretely `Uint8Array<ArrayBuffer>` and is. Writing the underlying
 * `ArrayBuffer` instead looks like it should work and does not -- the stream
 * rejects it, and since the rejection lands on a promise nothing awaits, the
 * whole thing simply hangs rather than failing. */
async function crunch(transform: { readable: ReadableStream<Uint8Array>; writable: WritableStream<BufferSource> }, bytes: Uint8Array): Promise<Uint8Array> {
  const view = new Uint8Array(bytes.byteLength);
  view.set(bytes);
  const writer = transform.writable.getWriter();
  void writer.write(view);
  void writer.close();
  return through(transform.readable);
}

/** A payload as a URL-safe string.
 *
 * Compression is used when the platform has it and skipped when it does not,
 * rather than pulling in a library: `CompressionStream` is in every browser this
 * project targets and in Node, and a link four times longer is a far smaller
 * problem than a dependency in the shared simulation module. */
export async function encodePlaytest(payload: PlaytestPayload): Promise<string> {
  const json = new TextEncoder().encode(JSON.stringify(payload));
  if (typeof CompressionStream === 'undefined') return `r${toBase64Url(json)}`;
  return `d${toBase64Url(await crunch(new CompressionStream('deflate-raw'), json))}`;
}

/** The inverse, validated. Throws `PlaytestError` on anything it cannot read,
 * because a half-understood room is worse than a refused one. */
export async function decodePlaytest(encoded: string): Promise<PlaytestPayload> {
  const encoding = encoded.slice(0, 1) as Encoding;
  const body = encoded.slice(1);
  let json: Uint8Array;
  try {
    const bytes = fromBase64Url(body);
    if (encoding === 'r') json = bytes;
    else if (encoding === 'd') {
      if (typeof DecompressionStream === 'undefined') throw new Error('no DecompressionStream');
      json = await crunch(new DecompressionStream('deflate-raw'), bytes);
    } else {
      throw new Error(`unknown encoding "${encoding}"`);
    }
  } catch (err) {
    throw new PlaytestError(`could not read the playtest link: ${err instanceof Error ? err.message : String(err)}`);
  }
  return loadPlaytest(JSON.parse(new TextDecoder().decode(json)));
}

/** The whole link, given where the game lives. `#playtest=<payload>`; the bare
 * `#playtest` still means "wait for a postMessage", so a desktop launch and a
 * link are the same feature with two deliveries rather than two features. */
export async function playtestLink(gameUrl: URL, payload: PlaytestPayload): Promise<string> {
  return `${gameUrl.origin}${gameUrl.pathname}#${PLAYTEST_HASH}=${await encodePlaytest(payload)}`;
}
